(() => {
  'use strict';

  if (window.NFDCartModulesLoader) return;

  const configNode = document.querySelector('[data-nfd-cart-loader]');
  if (!configNode) return;

  let config;
  try {
    config = JSON.parse(configNode.textContent || '{}');
  } catch (error) {
    console.error('Unable to read the cart customization loader configuration', error);
    return;
  }

  const assets = Array.isArray(config.assets) ? config.assets.filter(Boolean) : [];
  let initialPatchIncomplete = config.incompletePatch === true;
  const checkoutSelector = '[name="checkout"], a[href*="/checkout"], [data-nfd-checkout]';
  const acceleratedCheckoutSelector = window.NFDCheckoutGuard?.selector || '.additional-checkout-buttons, [data-shopify="dynamic-checkout-cart"]';
  const cartTriggerSelector = '[aria-controls="cart-drawer"], a[href$="/cart"], a[href*="/cart?"]';
  const updateInitialPatchLock = () => {
    document.documentElement.toggleAttribute('data-nfd-incomplete-patch', initialPatchIncomplete);
    if (!initialPatchIncomplete) return;
    document.querySelectorAll(checkoutSelector).forEach((control) => {
      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
      control.setAttribute('data-nfd-checkout-locked', '');
    });
    document.querySelectorAll(acceleratedCheckoutSelector).forEach((container) => {
      container.hidden = true;
      container.setAttribute('data-nfd-checkout-locked', '');
    });
  };
  const loader = {
    state: 'idle',
    promise: null,

    releaseInitialPatchLock() {
      if (!initialPatchIncomplete) return;
      initialPatchIncomplete = false;
      document.documentElement.removeAttribute('data-nfd-incomplete-patch');
      window.NFDCartCoordinator?.updateCheckoutControls?.();
    },

    load() {
      if (this.promise) return this.promise;
      this.state = 'loading';
      document.documentElement.dataset.nfdCartModules = 'loading';

      const pending = assets.map((source) => new Promise((resolve, reject) => {
        const existing = Array.from(document.scripts).find((script) => script.src === source);
        if (existing?.dataset.nfdLoaded === 'true') {
          resolve();
          return;
        }

        const script = existing || document.createElement('script');
        script.src = source;
        script.async = false;
        script.dataset.nfdModuleAsset = 'true';
        script.addEventListener('load', () => {
          script.dataset.nfdLoaded = 'true';
          resolve();
        }, { once: true });
        script.addEventListener('error', () => reject(new Error(`Cart module failed to load: ${source}`)), { once: true });
        if (!existing) document.head.append(script);
      }));

      this.promise = Promise.all(pending).then(async () => {
        await window.NFDHemmingLimits?.initializationPromise;
        if (!window.NFDCartCoordinator || !window.NFDHemmingConfirmation || !window.NFDHemmingLimits?.cartInstallerInstalled || !window.NFDPatchCustomization) {
          throw new Error('Cart customization modules are incomplete');
        }
        this.state = 'ready';
        document.documentElement.dataset.nfdCartModules = 'ready';
        document.dispatchEvent(new CustomEvent('nfd:cart-modules:ready'));
      }).catch((error) => {
        this.state = 'failed';
        document.documentElement.dataset.nfdCartModules = 'failed';
        document.dispatchEvent(new CustomEvent('nfd:cart-modules:error', { detail: { error } }));
        console.error('Cart customization could not be loaded', error);
        throw error;
      });
      return this.promise;
    }
  };

  Object.defineProperty(window, 'NFDCartModulesLoader', {
    configurable: false,
    enumerable: true,
    writable: false,
    value: loader
  });
  updateInitialPatchLock();

  const isCartTrigger = (target) => target instanceof Element && Boolean(target.closest(cartTriggerSelector));
  const isCheckout = (target) => target instanceof Element && Boolean(target.closest(checkoutSelector));
  const loadFromIntent = (event) => {
    if (isCartTrigger(event.target) || event.target instanceof Element && event.target.closest('cart-drawer')) {
      loader.load().catch(() => undefined);
    }
  };

  document.addEventListener('pointerover', loadFromIntent, { capture: true, passive: true });
  document.addEventListener('pointerdown', loadFromIntent, { capture: true, passive: true });
  document.addEventListener('focusin', loadFromIntent, true);
  document.addEventListener('cart:change', () => loader.load().catch(() => undefined));
  document.addEventListener('cart:refresh', () => loader.load().catch(() => undefined));

  document.addEventListener('click', (event) => {
    if (!isCheckout(event.target) || loader.state === 'ready') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const control = event.target.closest(checkoutSelector);
    loader.load().then(() => {
      if (control instanceof HTMLAnchorElement) {
        control.click();
      } else if (control?.form?.requestSubmit) {
        control.form.requestSubmit(control);
      } else {
        control?.click();
      }
    }).catch(() => undefined);
  }, true);

  document.addEventListener('submit', (event) => {
    if (!(window.NFDCheckoutGuard?.isCheckoutSubmission(event.target, event.submitter) ?? event.submitter?.name === 'checkout') || loader.state === 'ready') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const { submitter, target } = event;
    loader.load().then(() => submitter ? target.requestSubmit(submitter) : target.requestSubmit()).catch(() => undefined);
  }, true);

  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(() => {
      if (drawer.hasAttribute('open')) loader.load().catch(() => undefined);
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  if (config.immediate || window.NFDCheckoutGuard?.isBlocked() || drawer?.hasAttribute('open')) loader.load().catch(() => undefined);
})();
